import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, getDoc, query, where } from 'firebase/firestore';
import { db } from './firebase/config';
import { getCurrentUserOrgId, DEFAULT_ORG_ID } from './firebase/orgs';
import { getHomeworkNames, getMarkerSubmissionEmailsEnabled, updateMarkerSubmissionEmailsEnabled, sortHomeworkTypes } from './firebase/settings';
import { Bell, BellOff, Info } from 'lucide-react';
import { NOTIFICATION_LABELS, NOTIFICATION_PHASES } from './firebase/headMarkerNotifications';

const NotificationSettings = ({ user }) => {
  const [markers, setMarkers] = useState([]);
  const [homeworkTypes, setHomeworkTypes] = useState([]);
  const [settings, setSettings] = useState({});
  const [headMarkerPrefs, setHeadMarkerPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [activeTab, setActiveTab] = useState('headmarker'); // 'headmarker' or 'markers'
  // Per-org since 2026-09-28 - these settings used to be written to shared,
  // platform-wide docs (see notificationRefs below)
  const [orgId, setOrgId] = useState(null);
  const [submissionEmailsEnabled, setSubmissionEmailsEnabled] = useState(false);

  // Where this org's settings live. Marker choices: orgs/{orgId}/
  // notificationSettings/{homework} (read by the onSubmissionCreated email
  // function). Head marker preferences: orgs/{orgId}/settings/
  // headMarkerNotifications - the doc sendDirectNotification actually reads
  // (this page used to write a global settings/headMarkerNotifications doc
  // nothing read, so changes here never took effect).
  const markerNotifRef = (org, homework) => doc(db, 'orgs', org, 'notificationSettings', homework);
  const headMarkerPrefsRef = (org) => doc(db, 'orgs', org, 'settings', 'headMarkerNotifications');
  // Aileen's org only: the Google Sheet script still reads the old global
  // notificationSettings docs, so marker choices are mirrored there too.
  const mirrorToLegacy = (org) => org === DEFAULT_ORG_ID;

  useEffect(() => {
    loadAllSettings();
  }, []);

  const loadAllSettings = async () => {
    try {
      // Load homework types from settings
      const homeworkNamesObj = await getHomeworkNames();
      const types = Object.keys(homeworkNamesObj);
      
      // The tutor's own order if set, otherwise TH1-THn, RH1-RHn, then alphabetical
      const sorted = sortHomeworkTypes(types);
      types.splice(0, types.length, ...sorted);
      
      setHomeworkTypes(types);

      // Load this org's markers. Was an unfiltered read of every user on the
      // platform, which listed other tutors' markers here and fails outright
      // under the strict org read rules (2026-09-28).
      const currentOrgId = await getCurrentUserOrgId();
      setOrgId(currentOrgId);
      getMarkerSubmissionEmailsEnabled(currentOrgId).then(setSubmissionEmailsEnabled);
      const usersSnapshot = await getDocs(query(collection(db, 'users'), where('orgId', '==', currentOrgId), where('role', '==', 'marker')));
      const markersList = usersSnapshot.docs
        .filter(doc => doc.data().role === 'marker')
        .map(doc => ({
          id: doc.id,
          email: doc.data().email,
          name: doc.data().name,
          isActive: doc.data().isActive !== false
        }));
      setMarkers(markersList);

      // Load existing per-homework notification settings (for markers)
      const settingsObj = {};
      for (const homework of types) {
        let docSnap = await getDoc(markerNotifRef(currentOrgId, homework));
        // Aileen's org: fall back to the old shared doc until this homework's
        // per-org copy exists
        if (!docSnap.exists() && mirrorToLegacy(currentOrgId)) docSnap = await getDoc(doc(db, 'notificationSettings', homework));
        if (docSnap.exists()) {
          settingsObj[homework] = docSnap.data();
        } else {
          settingsObj[homework] = {
            markerEmails: [],
            headMarkerEnabled: false
          };
        }
      }
      setSettings(settingsObj);

      // Load head marker notification preferences
      const hmDocSnap = await getDoc(headMarkerPrefsRef(currentOrgId));
      if (hmDocSnap.exists()) {
        setHeadMarkerPrefs(hmDocSnap.data());
      } else {
        // Defaults - all off
        const defaults = {};
        Object.keys(NOTIFICATION_LABELS).forEach(type => {
          defaults[type] = false;
        });
        setHeadMarkerPrefs(defaults);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading notification settings:', error);
      setLoading(false);
    }
  };

  // Toggle a head marker notification preference
  const toggleHeadMarkerPref = async (notificationType) => {
    try {
      setSaving(true);
      const updated = {
        ...headMarkerPrefs,
        [notificationType]: !headMarkerPrefs[notificationType],
        // Always save head marker contact info so the notification utility can find it
        headMarkerEmail: user?.email || headMarkerPrefs.headMarkerEmail || '',
        headMarkerName: user?.name || headMarkerPrefs.headMarkerName || 'Head Marker'
      };

      await setDoc(headMarkerPrefsRef(orgId), updated);
      setHeadMarkerPrefs(updated);
      setSaving(false);
    } catch (error) {
      console.error('Error updating head marker preference:', error);
      setSaving(false);
    }
  };

  // Toggle all head marker notifications on or off
  const toggleAllHeadMarkerPrefs = async (enabled) => {
    try {
      setSaving(true);
      const updated = {
        headMarkerEmail: user?.email || headMarkerPrefs.headMarkerEmail || '',
        headMarkerName: user?.name || headMarkerPrefs.headMarkerName || 'Head Marker'
      };
      Object.keys(NOTIFICATION_LABELS).forEach(type => {
        updated[type] = enabled;
      });

      await setDoc(headMarkerPrefsRef(orgId), updated);
      setHeadMarkerPrefs(updated);
      setSaving(false);
    } catch (error) {
      console.error('Error updating all head marker preferences:', error);
      setSaving(false);
    }
  };

  // Existing marker notification toggles
  const toggleMarkerNotification = async (homework, markerEmail) => {
    try {
      setSaving(true);
      const currentSettings = settings[homework] || { markerEmails: [], headMarkerEnabled: false };
      const markerEmails = currentSettings.markerEmails || [];
      
      let updatedEmails;
      if (markerEmails.includes(markerEmail)) {
        updatedEmails = markerEmails.filter(email => email !== markerEmail);
      } else {
        updatedEmails = [...markerEmails, markerEmail];
      }

      const updatedSettings = {
        ...currentSettings,
        markerEmails: updatedEmails
      };

      await setDoc(markerNotifRef(orgId, homework), updatedSettings);
      if (mirrorToLegacy(orgId)) await setDoc(doc(db, 'notificationSettings', homework), updatedSettings);
      
      setSettings(prev => ({
        ...prev,
        [homework]: updatedSettings
      }));
      setSaving(false);
    } catch (error) {
      console.error('Error updating marker notification:', error);
      setSaving(false);
    }
  };

  const toggleHeadMarkerHomeworkNotification = async (homework) => {
    try {
      setSaving(true);
      const currentSettings = settings[homework] || { markerEmails: [], headMarkerEnabled: false };
      
      const updatedSettings = {
        ...currentSettings,
        headMarkerEnabled: !currentSettings.headMarkerEnabled
      };

      await setDoc(markerNotifRef(orgId, homework), updatedSettings);
      if (mirrorToLegacy(orgId)) await setDoc(doc(db, 'notificationSettings', homework), updatedSettings);
      
      setSettings(prev => ({
        ...prev,
        [homework]: updatedSettings
      }));
      setSaving(false);
    } catch (error) {
      console.error('Error updating head marker notification:', error);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>
        Loading notification settings...
      </div>
    );
  }

  const displayedMarkers = showInactive ? markers : markers.filter(m => m.isActive);
  
  const enabledCount = Object.values(headMarkerPrefs).filter(Boolean).length;
  const totalCount = Object.keys(NOTIFICATION_LABELS).length;

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    }}>
      <h2 style={{ margin: '0 0 24px 0', fontSize: '22px', fontWeight: '700', color: '#2c3e50' }}>
        Email Notification Settings
      </h2>

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        <button
          onClick={() => setActiveTab('headmarker')}
          style={{
            padding: '10px 20px',
            background: activeTab === 'headmarker' ? '#c33329' : '#ecf0f1',
            color: activeTab === 'headmarker' ? 'white' : '#2c3e50',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px'
          }}
        >
          Head Marker Alerts
        </button>
        <button
          onClick={() => setActiveTab('markers')}
          style={{
            padding: '10px 20px',
            background: activeTab === 'markers' ? '#c33329' : '#ecf0f1',
            color: activeTab === 'markers' ? 'white' : '#2c3e50',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px'
          }}
        >
          Marker Homework Notifications
        </button>
      </div>

      {/* HEAD MARKER ALERTS TAB */}
      {activeTab === 'headmarker' && (
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            padding: '12px 16px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '14px', color: '#4a5568' }}>
              {enabledCount} of {totalCount} notifications enabled
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => toggleAllHeadMarkerPrefs(true)}
                disabled={saving}
                style={{
                  padding: '6px 14px',
                  background: '#1b998b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '13px',
                  opacity: saving ? 0.6 : 1
                }}
              >
                Enable All
              </button>
              <button
                onClick={() => toggleAllHeadMarkerPrefs(false)}
                disabled={saving}
                style={{
                  padding: '6px 14px',
                  background: '#7f8c8d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '13px',
                  opacity: saving ? 0.6 : 1
                }}
              >
                Disable All
              </button>
            </div>
          </div>

          {/* Notification groups */}
          {Object.entries(NOTIFICATION_PHASES).map(([groupName, types]) => (
            <div key={groupName} style={{ marginBottom: '24px' }}>
              <h3 style={{
                fontSize: '15px',
                fontWeight: '700',
                color: '#4a5568',
                margin: '0 0 12px 0',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                {groupName}
              </h3>
              
              <div style={{
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                {types.map((type, idx) => {
                  const info = NOTIFICATION_LABELS[type];
                  const isEnabled = headMarkerPrefs[type] || false;
                  const isScheduled = groupName === 'Scheduled Alerts';
                  
                  return (
                    <div
                      key={type}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '14px 16px',
                        borderBottom: idx < types.length - 1 ? '1px solid #e2e8f0' : 'none',
                        background: isEnabled ? '#f0fdf4' : 'white',
                        transition: 'background 0.2s ease'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#2c3e50',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          {info.label}
                          {isScheduled && (
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 8px',
                              background: '#f39c12',
                              color: 'white',
                              borderRadius: '10px',
                              fontWeight: '500'
                            }}>
                              Phase 3
                            </span>
                          )}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#7f8c8d',
                          marginTop: '2px'
                        }}>
                          {info.description}
                        </div>
                      </div>
                      
                      <button
                        onClick={() => toggleHeadMarkerPref(type)}
                        disabled={saving || isScheduled}
                        title={isScheduled ? 'Coming soon - scheduled notifications' : (isEnabled ? 'Disable notification' : 'Enable notification')}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '48px',
                          height: '32px',
                          borderRadius: '16px',
                          border: 'none',
                          cursor: (saving || isScheduled) ? 'not-allowed' : 'pointer',
                          background: isScheduled ? '#e2e8f0' : (isEnabled ? '#1b998b' : '#cbd5e0'),
                          transition: 'background 0.2s ease',
                          opacity: isScheduled ? 0.5 : 1,
                          position: 'relative'
                        }}
                      >
                        <div style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          background: 'white',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          position: 'absolute',
                          left: isEnabled ? '20px' : '4px',
                          transition: 'left 0.2s ease'
                        }} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Info box */}
          <div style={{
            display: 'flex',
            gap: '10px',
            padding: '14px 16px',
            background: '#ebf8ff',
            borderRadius: '8px',
            border: '1px solid #bee3f8',
            marginTop: '16px'
          }}>
            <Info size={18} style={{ color: '#3182ce', flexShrink: 0, marginTop: '2px' }} />
            <div style={{ fontSize: '13px', color: '#2c5282', lineHeight: '1.5' }}>
              Notifications are sent to the head marker email address registered in the system.
              Scheduled alerts (Phase 3) will run automatically once configured. All other
              notifications are triggered in real-time when the action occurs.
            </div>
          </div>
        </div>
      )}

      {/* MARKER HOMEWORK NOTIFICATIONS TAB */}
      {activeTab === 'markers' && (
        <div>
          {/* Org-wide on/off for the new-submission emails below */}
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '14px 16px', marginBottom: '16px',
            background: submissionEmailsEnabled ? '#f0fdf4' : '#f8f9fa',
            border: `1px solid ${submissionEmailsEnabled ? '#86efac' : '#e2e8f0'}`, borderRadius: '8px', cursor: saving ? 'wait' : 'pointer'
          }}>
            <input
              type="checkbox"
              checked={submissionEmailsEnabled}
              disabled={saving || !orgId}
              onChange={async (e) => {
                const enabled = e.target.checked;
                setSaving(true);
                try {
                  await updateMarkerSubmissionEmailsEnabled(enabled, orgId);
                  setSubmissionEmailsEnabled(enabled);
                } catch (err) {
                  alert('Could not save this setting. Please try again.');
                } finally {
                  setSaving(false);
                }
              }}
              style={{ marginTop: '3px' }}
            />
            <span>
              <strong style={{ fontSize: '14px', color: '#2c3e50' }}>Email markers when homework is submitted</strong>
              <span style={{ display: 'block', fontSize: '13px', color: '#4a5568', marginTop: '2px' }}>
                Each marker ticked for a homework below gets an email as soon as a student submits it. Markers can also change their own choices from their dashboard.
              </span>
            </span>
          </label>
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: '16px'
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#4a5568' }}>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                style={{ borderRadius: '4px' }}
              />
              Show inactive markers
            </label>
          </div>

          <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#7f8c8d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '2px solid #e2e8f0'
                  }}>
                    Marker
                  </th>
                  {homeworkTypes.map(homework => (
                    <th key={homework} style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#7f8c8d',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      borderBottom: '2px solid #e2e8f0'
                    }}>
                      {homework}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Head Marker row */}
                <tr style={{ background: '#ebf8ff' }}>
                  <td style={{
                    padding: '12px 16px',
                    fontSize: '14px',
                    fontWeight: '700',
                    color: '#2c3e50',
                    borderBottom: '1px solid #e2e8f0'
                  }}>
                    Head Marker
                  </td>
                  {homeworkTypes.map(homework => {
                    const homeworkSettings = settings[homework] || { markerEmails: [], headMarkerEnabled: false };
                    return (
                      <td key={homework} style={{
                        padding: '12px 8px',
                        textAlign: 'center',
                        borderBottom: '1px solid #e2e8f0'
                      }}>
                        <button
                          onClick={() => toggleHeadMarkerHomeworkNotification(homework)}
                          disabled={saving}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {homeworkSettings.headMarkerEnabled ? (
                            <Bell size={20} color="#1b998b" />
                          ) : (
                            <BellOff size={20} color="#cbd5e0" />
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
                {/* Marker rows */}
                {displayedMarkers.map(marker => (
                  <tr key={marker.id} style={{
                    background: !marker.isActive ? '#f8f9fa' : 'white'
                  }}>
                    <td style={{
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: !marker.isActive ? '#a0aec0' : '#2c3e50',
                      borderBottom: '1px solid #e2e8f0'
                    }}>
                      {marker.name}
                      {!marker.isActive && (
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '11px',
                          color: '#a0aec0',
                          fontWeight: '400'
                        }}>
                          (Inactive)
                        </span>
                      )}
                    </td>
                    {homeworkTypes.map(homework => {
                      const homeworkSettings = settings[homework] || { markerEmails: [], headMarkerEnabled: false };
                      const isEnabled = homeworkSettings.markerEmails?.includes(marker.email);
                      return (
                        <td key={homework} style={{
                          padding: '12px 8px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e2e8f0'
                        }}>
                          <button
                            onClick={() => toggleMarkerNotification(homework, marker.email)}
                            disabled={saving || !marker.isActive}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: (saving || !marker.isActive) ? 'not-allowed' : 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: !marker.isActive ? 0.4 : 1
                            }}
                          >
                            {isEnabled ? (
                              <Bell size={20} color="#1b998b" />
                            ) : (
                              <BellOff size={20} color="#cbd5e0" />
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{
            marginTop: '12px',
            fontSize: '13px',
            color: '#7f8c8d',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Bell size={16} color="#1b998b" /> Enabled
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <BellOff size={16} color="#cbd5e0" /> Disabled
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;